import { useMemo, useState } from 'react';
import {
  Drawer,
  List,
  Tag,
  Button,
  Input,
  Select,
  Empty,
  Space,
  Typography,
  Tooltip,
  Popconfirm,
  message,
  Divider,
} from 'antd';
import { SearchOutlined, StarFilled, LockOutlined, TeamOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { PlayerCard, PlayerSquadSlot } from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
  userId: string;
  squadType: 'home' | 'away';
  position: string;
  currentSlot?: PlayerSquadSlot;
  allCards: PlayerCard[];
  onAssigned: () => void;
}

// Strict role per XI slot — must match exactly. `wk` means the card's
// player_masters.isWicketKeeper flag is true; otherwise the number is the
// required player_cards.playerType (0=Batsman, 1=Bowler, 2=All-Rounder).
// Subs are absent from this map and accept any role.
const POSITION_ROLE_PREF: Record<string, { type?: 0 | 1 | 2; wk: boolean; label: string }> = {
  opener_1: { type: 0, wk: false, label: 'Batsman' },
  opener_2: { type: 0, wk: false, label: 'Batsman' },
  batsman_3: { type: 0, wk: false, label: 'Batsman' },
  batsman_4: { type: 0, wk: false, label: 'Batsman' },
  batsman_5: { type: 0, wk: false, label: 'Batsman' },
  all_rounder_6: { type: 2, wk: false, label: 'All-Rounder' },
  wicket_keeper: { wk: true, label: 'Wicket-Keeper' },
  bowler_8: { type: 1, wk: false, label: 'Bowler' },
  bowler_9: { type: 1, wk: false, label: 'Bowler' },
  bowler_10: { type: 1, wk: false, label: 'Bowler' },
  bowler_11: { type: 1, wk: false, label: 'Bowler' },
};

const TYPE_LABEL: Record<number, string> = {
  0: 'Batsman',
  1: 'Bowler',
  2: 'All-Rounder',
};

// Role label for display: a wicket-keeper is shown as "Wicket-Keeper"
// regardless of the underlying playerType (almost always 0=Batsman).
const roleLabel = (card: { playerType?: number; isWicketKeeper?: boolean }): string => {
  if (card.isWicketKeeper) return 'Wicket-Keeper';
  return TYPE_LABEL[card.playerType ?? -1] ?? '-';
};

const RARITY_LABEL: Record<number, string> = {
  0: 'Common',
  1: 'Rare',
  2: 'Epic',
  3: 'Legendary',
};

const RARITY_COLOR: Record<number, string> = {
  0: 'default',
  1: 'blue',
  2: 'purple',
  3: 'gold',
};

const formatPosition = (pos: string): string =>
  pos.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const cardMatchesSlotPref = (
  card: PlayerCard,
  pref?: { type?: 0 | 1 | 2; wk: boolean }
): boolean => {
  if (!pref) return false;
  if (pref.wk) return !!card.isWicketKeeper;
  // A WK-Batsman is reserved for the WK slot — never matches a regular
  // Batsman / Bowler / AR slot.
  if (card.isWicketKeeper) return false;
  return pref.type !== undefined && card.playerType === pref.type;
};

type RoleFilter = 'all' | 'recommended' | 'bat' | 'bowl' | 'ar' | 'wk';

export function SquadSlotEditor({
  open,
  onClose,
  userId,
  squadType,
  position,
  currentSlot,
  allCards,
  onAssigned,
}: Props) {
  const [filter, setFilter] = useState<RoleFilter>('all');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState<string | null>(null);

  const pref = POSITION_ROLE_PREF[position];
  const positionLabel = formatPosition(position);

  // Eligible: cards not in any squad, plus the card currently in this slot
  // (so admin can see what's there and the "Currently here" pill works).
  const eligibleCards = useMemo(
    () => allCards.filter((c) => !c.isInSquad || c.id === currentSlot?.playerCardId),
    [allCards, currentSlot?.playerCardId]
  );

  const filteredCards = useMemo(() => {
    let cs = eligibleCards;
    const q = search.trim().toLowerCase();
    if (q) {
      cs = cs.filter((c) => `${c.firstName ?? ''} ${c.secondName ?? ''}`.toLowerCase().includes(q));
    }
    switch (filter) {
      case 'recommended':
        cs = cs.filter((c) => cardMatchesSlotPref(c, pref));
        break;
      // WK-Batsmen are categorised as Wicket-Keepers, so the Batsman /
      // Bowler / All-Rounder filters explicitly exclude them. They show
      // up only under the Wicket-Keepers filter.
      case 'bat':
        cs = cs.filter((c) => c.playerType === 0 && !c.isWicketKeeper);
        break;
      case 'bowl':
        cs = cs.filter((c) => c.playerType === 1 && !c.isWicketKeeper);
        break;
      case 'ar':
        cs = cs.filter((c) => c.playerType === 2 && !c.isWicketKeeper);
        break;
      case 'wk':
        cs = cs.filter((c) => !!c.isWicketKeeper);
        break;
    }
    return cs;
  }, [eligibleCards, search, filter, pref]);

  // Order: currently-assigned first, then recommended, then rest — each
  // sub-bucket sorted by rating descending.
  const sortedCards = useMemo(() => {
    return [...filteredCards].sort((a, b) => {
      const aCur = a.id === currentSlot?.playerCardId ? 1 : 0;
      const bCur = b.id === currentSlot?.playerCardId ? 1 : 0;
      if (aCur !== bCur) return bCur - aCur;
      const aRec = cardMatchesSlotPref(a, pref) ? 1 : 0;
      const bRec = cardMatchesSlotPref(b, pref) ? 1 : 0;
      if (aRec !== bRec) return bRec - aRec;
      return (b.overallRating ?? 0) - (a.overallRating ?? 0);
    });
  }, [filteredCards, pref, currentSlot?.playerCardId]);

  const recommendedCount = useMemo(
    () =>
      eligibleCards.filter(
        (c) => cardMatchesSlotPref(c, pref) && c.id !== currentSlot?.playerCardId
      ).length,
    [eligibleCards, pref, currentSlot?.playerCardId]
  );

  const handleSubmit = async (cardId: string | null) => {
    setSubmitting(cardId ?? '__clear__');
    try {
      await adminApi.assignSquadSlot(userId, squadType, position, cardId);
      message.success(cardId ? 'Card assigned.' : 'Slot cleared.');
      onAssigned();
      onClose();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update slot';
      message.error(msg);
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <TeamOutlined />
            <Typography.Text strong style={{ fontSize: 16 }}>
              {positionLabel}
            </Typography.Text>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {squadType.toUpperCase()} squad · {pref ? `Recommended: ${pref.label}` : 'Any role'}
          </Typography.Text>
        </Space>
      }
      width={560}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {currentSlot?.playerCardId && (
        <div
          style={{
            padding: 12,
            background: '#fffbe6',
            border: '1px solid #ffe58f',
            borderRadius: 6,
            marginBottom: 16,
          }}
        >
          <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
            <Space direction="vertical" size={2}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Currently assigned
              </Typography.Text>
              <Space size={6} wrap>
                <Typography.Text strong>
                  {currentSlot.firstName} {currentSlot.secondName}
                </Typography.Text>
                <Tag color={currentSlot.isWicketKeeper ? 'cyan' : undefined}>
                  {roleLabel(currentSlot)}
                </Tag>
                <Tag color="blue">Rating {currentSlot.overallRating ?? 0}</Tag>
              </Space>
            </Space>
            <Popconfirm
              title="Clear this slot?"
              description="Removes the card from this position. The card returns to the user's collection."
              okText="Clear"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleSubmit(null)}
            >
              <Button danger size="small" loading={submitting === '__clear__'}>
                Clear
              </Button>
            </Popconfirm>
          </Space>
        </div>
      )}

      <Space.Compact style={{ width: '100%', marginBottom: 8 }}>
        <Input
          allowClear
          placeholder="Search by name..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1 }}
        />
        <Select
          value={filter}
          onChange={(v) => setFilter(v)}
          style={{ width: 180 }}
          options={[
            {
              label: `Recommended (${recommendedCount})`,
              value: 'recommended',
              disabled: !pref || recommendedCount === 0,
            },
            { label: 'All roles', value: 'all' },
            { label: 'Batsmen', value: 'bat' },
            { label: 'Bowlers', value: 'bowl' },
            { label: 'All-Rounders', value: 'ar' },
            { label: 'Wicket-Keepers', value: 'wk' },
          ]}
        />
      </Space.Compact>

      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {sortedCards.length} card{sortedCards.length === 1 ? '' : 's'} · sorted by rating
        (recommended on top)
      </Typography.Text>

      <Divider style={{ margin: '12px 0' }} />

      {sortedCards.length === 0 ? (
        <Empty
          description={
            eligibleCards.length === 0
              ? 'User has no spare cards to assign'
              : 'No cards match the current filter'
          }
          style={{ marginTop: 32 }}
        />
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={sortedCards}
          renderItem={(card) => {
            const isCurrent = card.id === currentSlot?.playerCardId;
            const isRecommended = !isCurrent && cardMatchesSlotPref(card, pref);
            return (
              <List.Item
                style={{
                  padding: '10px 12px',
                  background: isCurrent ? '#fffbe6' : isRecommended ? '#f6ffed' : undefined,
                  borderRadius: 4,
                  border: isCurrent
                    ? '1px solid #ffe58f'
                    : isRecommended
                      ? '1px solid #b7eb8f'
                      : '1px solid transparent',
                  marginBottom: 6,
                }}
                actions={[
                  isCurrent ? (
                    <Tag color="gold" key="cur">
                      Currently here
                    </Tag>
                  ) : (
                    <Popconfirm
                      key="assign"
                      title={`Assign to ${positionLabel}?`}
                      description={
                        currentSlot?.playerCardId
                          ? 'This replaces the currently-assigned card.'
                          : undefined
                      }
                      okText="Assign"
                      onConfirm={() => handleSubmit(card.id)}
                    >
                      <Button type="primary" size="small" loading={submitting === card.id}>
                        Assign
                      </Button>
                    </Popconfirm>
                  ),
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space size={6} wrap>
                      <Typography.Text strong>
                        {card.firstName} {card.secondName}
                      </Typography.Text>
                      {isRecommended && (
                        <Tooltip title={`Matches ${pref?.label ?? ''}`}>
                          <Tag color="green" icon={<StarFilled />}>
                            Recommended
                          </Tag>
                        </Tooltip>
                      )}
                      {card.isLocked && (
                        <Tooltip title="Locked card">
                          <LockOutlined />
                        </Tooltip>
                      )}
                    </Space>
                  }
                  description={
                    <Space wrap size={[6, 4]}>
                      <Tag color={card.isWicketKeeper ? 'cyan' : undefined}>{roleLabel(card)}</Tag>
                      <Tag color={RARITY_COLOR[card.rarity] ?? 'default'}>
                        {RARITY_LABEL[card.rarity] ?? 'Common'}
                      </Tag>
                      <Typography.Text strong>Rating {card.overallRating}</Typography.Text>
                      <Typography.Text type="secondary">
                        Lvl {card.playerLevel} · Evo {card.evolutionLevel}
                      </Typography.Text>
                      <Typography.Text type="secondary">
                        BAT {card.battingAggregate} · BWL {card.bowlingAggregate} · FLD{' '}
                        {card.fieldingAggregate} · PHY {card.physicalAggregate}
                      </Typography.Text>
                    </Space>
                  }
                />
              </List.Item>
            );
          }}
        />
      )}
    </Drawer>
  );
}
